import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop
 * --------------------------------------------------------------------------
 * React Router does NOT restore scroll position between SPA route changes —
 * the document keeps whatever scroll offset the previous page left behind.
 * On mobile this creates a jarring UX: a user scrolls deep on a listing page,
 * taps a product card, and the detail page mounts already scrolled half-way
 * down (often past the hero/image). This component listens to pathname
 * changes and resets the window scroll to 0 on every navigation.
 *
 * The `history.scrollRestoration = 'manual'` line opts us out of the browser's
 * native restore-on-back behaviour so the manual reset is consistent for
 * forward navigations, link clicks, and back button presses.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Use 'instant' (not smooth) — users expect a fresh page to start at the
    // top immediately, not animate from the previous offset.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

export default ScrollToTop;
